<template>
  <Teleport to="body">
    <Transition name="wrk-glass-fade">
      <div v-if="visible" class="wrk-glass-screen" role="presentation">
        <div class="wrk-glass-screen__blur-deep" aria-hidden="true" />
        <div class="wrk-glass-screen__blur-mid" aria-hidden="true" />
        <div class="wrk-glass-screen__tint" aria-hidden="true" />
        <div class="wrk-glass-screen__glow wrk-glass-screen__glow--tr" aria-hidden="true" />
        <div class="wrk-glass-screen__glow wrk-glass-screen__glow--bl" aria-hidden="true" />
        <div class="wrk-glass-screen__noise" aria-hidden="true" />

        <div class="wrk-glass-screen__content wrk-overlay-root">
          <div class="wrk-loader-shell" role="status" aria-live="polite" aria-busy="true">
            <div class="wrk-loader-shell__glow" aria-hidden="true" />
            <div class="wrk-loader-shell__sheen" aria-hidden="true" />
            <div class="wrk-loader-brand-mark">
              <v-img
                :src="sidebarLogo"
                alt="ServiceWRK"
                width="56"
                height="56"
                cover
                class="wrk-loader-logo-img"
              />
            </div>

            <div class="wrk-loader-dots" aria-hidden="true">
              <span class="wrk-loader-dot" />
              <span class="wrk-loader-dot" />
              <span class="wrk-loader-dot" />
            </div>

            <p class="wrk-loader-message">Loading, please wait...</p>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script>
import { getGlobalLoaderState } from "@/utils/globalLoader.js";
import sidebarLogo from "@/assets/logo2.jpg";

export default {
  name: "GlobalLoader",

  data: () => ({
    loaderState: getGlobalLoaderState(),
    sidebarLogo,
  }),

  computed: {
    visible() {
      return this.loaderState.count > 0;
    },
  },

  watch: {
    visible: {
      immediate: true,
      handler(active) {
        document.documentElement.classList.toggle("sw-loader-active", !!active);
      },
    },
  },

  beforeUnmount() {
    document.documentElement.classList.remove("sw-loader-active");
  },
};
</script>

<style>
/* Blur the live app behind the glass (loader is teleported outside #app) */
html.sw-loader-active #app .v-application {
  filter: blur(6px) saturate(1.08);
  transition: filter 0.3s ease;
  pointer-events: none;
  user-select: none;
}

html[data-theme="dark"].sw-loader-active #app .v-application {
  filter: blur(8px) saturate(1.05) brightness(0.96);
}

.wrk-glass-screen {
  position: fixed;
  inset: 0;
  z-index: 10000;
  isolation: isolate;
  pointer-events: all;
}

.wrk-glass-screen__blur-deep,
.wrk-glass-screen__blur-mid,
.wrk-glass-screen__tint,
.wrk-glass-screen__glow,
.wrk-glass-screen__noise {
  position: absolute;
  inset: 0;
  pointer-events: none;
}

.wrk-glass-screen__blur-deep {
  background: var(--sw-loader-scrim-bg);
  backdrop-filter: var(--sw-loader-scrim-blur);
  -webkit-backdrop-filter: var(--sw-loader-scrim-blur);
}

.wrk-glass-screen__blur-mid {
  background: rgba(255, 255, 255, 0.06);
  backdrop-filter: blur(12px) saturate(140%);
  -webkit-backdrop-filter: blur(12px) saturate(140%);
}

.wrk-glass-screen__tint {
  background: var(--sw-loader-scrim-gradient);
}

.wrk-glass-screen__glow {
  inset: auto;
  width: min(52vw, 420px);
  height: min(52vw, 420px);
  border-radius: 50%;
}

.wrk-glass-screen__glow--tr {
  top: -12%;
  right: -8%;
  background: var(--sw-loader-scrim-glow);
}

.wrk-glass-screen__glow--bl {
  bottom: -18%;
  left: -10%;
  width: min(44vw, 360px);
  height: min(44vw, 360px);
  background: radial-gradient(
    circle,
    rgba(155, 45, 173, 0.14) 0%,
    transparent 70%
  );
}

.wrk-glass-screen__noise {
  opacity: 0.04;
  mix-blend-mode: overlay;
  background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 512 512' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E");
}

html[data-theme="dark"] .wrk-glass-screen__blur-mid {
  background: rgba(12, 14, 20, 0.12);
}

html[data-theme="dark"] .wrk-glass-screen__glow--bl {
  background: radial-gradient(
    circle,
    rgba(155, 45, 173, 0.18) 0%,
    transparent 70%
  );
}

.wrk-glass-screen__content {
  position: relative;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
}

.wrk-glass-fade-enter-active,
.wrk-glass-fade-leave-active {
  transition: opacity 0.35s ease;
}

.wrk-glass-fade-enter-from,
.wrk-glass-fade-leave-to {
  opacity: 0;
}

html.sw-loader-active,
html.sw-loader-active body {
  overflow: hidden !important;
}

html.sw-loader-active .v-data-table .v-progress-linear,
html.sw-loader-active .v-data-table .v-data-table-progress,
html.sw-loader-active .v-table--loading .v-progress-linear {
  display: none !important;
}
</style>

<style scoped>
.wrk-overlay-root {
  --wrk-accent: rgb(var(--sw-primary-rgb, 123, 4, 108));
  --wrk-loader-notch-r: 14px;
  --wrk-loader-notch-top: 50%;
}

.wrk-loader-shell {
  position: relative;
  isolation: isolate;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0;
  min-width: 240px;
  padding: 2rem 2.75rem 1.85rem;
  border-radius: 24px;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.52);
  border: 1px solid rgba(255, 255, 255, 0.72);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.82);
  backdrop-filter: blur(24px) saturate(1.45);
  -webkit-backdrop-filter: blur(24px) saturate(1.45);
  filter:
    drop-shadow(0 8px 32px rgba(123, 4, 108, 0.14))
    drop-shadow(0 2px 10px rgba(123, 4, 108, 0.06));
  -webkit-mask-image:
    linear-gradient(#000 0 0),
    radial-gradient(
      circle var(--wrk-loader-notch-r) at 0 var(--wrk-loader-notch-top),
      #000 99.5%,
      transparent 100%
    ),
    radial-gradient(
      circle var(--wrk-loader-notch-r) at 100% var(--wrk-loader-notch-top),
      #000 99.5%,
      transparent 100%
    );
  -webkit-mask-composite: source-out, source-out;
  mask-image:
    linear-gradient(#000 0 0),
    radial-gradient(
      circle var(--wrk-loader-notch-r) at 0 var(--wrk-loader-notch-top),
      #000 99.5%,
      transparent 100%
    ),
    radial-gradient(
      circle var(--wrk-loader-notch-r) at 100% var(--wrk-loader-notch-top),
      #000 99.5%,
      transparent 100%
    );
  mask-composite: exclude, exclude;
}

.wrk-loader-shell__sheen,
.wrk-loader-shell__glow {
  position: absolute;
  pointer-events: none;
  z-index: 0;
}

.wrk-loader-shell__sheen {
  inset: 0;
  border-radius: inherit;
  background: linear-gradient(
    148deg,
    rgba(255, 255, 255, 0.78) 0%,
    rgba(248, 240, 252, 0.52) 45%,
    rgba(192, 38, 211, 0.1) 100%
  );
}

.wrk-loader-shell__glow {
  top: -35%;
  right: -18%;
  width: 220px;
  height: 220px;
  border-radius: 50%;
  background: radial-gradient(
    circle,
    rgba(192, 38, 211, 0.2) 0%,
    transparent 68%
  );
}

.wrk-loader-brand-mark,
.wrk-loader-dots,
.wrk-loader-message {
  position: relative;
  z-index: 1;
}

.wrk-loader-brand-mark {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  padding: 4px;
  margin-bottom: 1.35rem;
  border-radius: 16px;
  background: var(--sw-surface, #fff);
  border: 1px solid rgba(var(--sw-primary-rgb, 123, 4, 108), 0.12);
  box-shadow:
    0 4px 18px rgba(var(--sw-primary-rgb, 123, 4, 108), 0.16),
    inset 0 1px 0 rgba(255, 255, 255, 0.5);
  overflow: hidden;
  flex-shrink: 0;
}

.wrk-loader-logo-img {
  border-radius: 12px;
}

.wrk-loader-dots {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  min-height: 24px;
  margin-bottom: 1rem;
}

.wrk-loader-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: var(--wrk-accent);
  animation: wrk-dot-bounce 1.2s ease-in-out infinite;
}

.wrk-loader-dot:nth-child(2) {
  animation-delay: 0.15s;
}

.wrk-loader-dot:nth-child(3) {
  animation-delay: 0.3s;
}

.wrk-loader-message {
  margin: 0;
  font-size: 0.92rem;
  font-weight: 500;
  color: var(--sw-text, #374151);
  letter-spacing: 0.01em;
}

html[data-theme="dark"] .wrk-loader-message {
  color: var(--sw-text-secondary, rgba(255, 255, 255, 0.78));
}

html[data-theme="dark"] .wrk-loader-shell {
  background: rgba(20, 18, 28, 0.58);
  border-color: rgba(255, 255, 255, 0.14);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.08),
    0 18px 48px rgba(0, 0, 0, 0.28);
}

html[data-theme="dark"] .wrk-loader-shell__sheen {
  background: linear-gradient(
    148deg,
    rgba(255, 255, 255, 0.12) 0%,
    rgba(123, 4, 108, 0.1) 45%,
    rgba(192, 38, 211, 0.16) 100%
  );
}

@keyframes wrk-dot-bounce {
  0%,
  80%,
  100% {
    transform: translateY(0);
    opacity: 0.45;
  }

  40% {
    transform: translateY(-9px);
    opacity: 1;
  }
}

@media (prefers-reduced-motion: reduce) {
  .wrk-loader-dot {
    animation: none !important;
    opacity: 0.85;
  }
}

@media (max-width: 960px) {
  .wrk-overlay-root {
    --wrk-loader-notch-r: 0;
  }

  .wrk-loader-shell {
    min-width: min(240px, calc(100vw - 32px));
    max-width: calc(100vw - 32px);
    padding: 1.8rem 2rem 1.65rem;
    -webkit-mask-image: none;
    mask-image: none;
  }
}
</style>
